import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { CalendarClock, Map as MapIcon, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import {
  formatTime,
  Timestamp,
  type Activity,
  type ActivityUpdate,
  type Trip,
} from "@travelix/core";
import { getCategoryMeta } from "@/lib/activity-categories";
import { CategoryPicker } from "@/components/category-picker";

import { cn } from "@/lib/utils";
import { useTrip, useTripActivities } from "@/hooks/use-trip";
import { useTripMutation } from "@/hooks/use-trip-mutation";
import { useTripWeather } from "@/hooks/use-trip-weather";
import { EmptyState } from "@/components/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { ConfirmDialog } from "@/components/ui/dialog";
import { WeatherPill } from "@/components/weather-pill";
import type { WeatherByDate } from "@/lib/weather";
import { isTripReadOnly } from "@/lib/trip-readonly";
import {
  getTripDateBounds,
  isWithinBounds,
  toDateTimeLocal,
} from "@/lib/trip-date-bounds";
import { deleteActivity, setTripDayMapsUrl, updateActivity, unwrap, queryKeys } from "@/lib/api";

export default function TimelineTab() {
  const { id } = useParams<{ id: string }>();
  const { data: activities, isLoading } = useTripActivities(id);
  const { data: trip } = useTrip(id);
  const { data: weather } = useTripWeather(trip);
  const [editing, setEditing] = useState<Activity | null>(null);
  const readOnly = isTripReadOnly(trip);

  return (
    <>
      {isLoading ? (
        <LoadingList />
      ) : !activities || activities.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={readOnly ? "Nothing was scheduled" : "Nothing scheduled yet"}
          body={
            readOnly
              ? "This trip is in the past — no more items can be added."
              : "Add the first stop — a flight, a check-in, a dinner. The group sees it right away."
          }
        />
      ) : (
        <TimelineList
          trip={trip}
          activities={activities}
          onEdit={readOnly ? undefined : setEditing}
          weather={weather ?? {}}
        />
      )}

      {/* Edit sheet only mounts for editable trips */}
      {editing && id && !readOnly ? (
        <EditEventSheet
          tripId={id}
          activity={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

// ─── List ────────────────────────────────────────────────────
function TimelineList({
  trip,
  activities,
  onEdit,
  weather,
}: {
  trip: Trip | undefined;
  activities: Activity[];
  /** Undefined means read-only — rows render as non-interactive divs. */
  onEdit?: (a: Activity) => void;
  weather: WeatherByDate;
}) {
  const groups = groupByDay(activities, trip);
  const readOnly = !onEdit;
  const groupRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [activeDay, setActiveDay] = useState<string>(groups[0]?.key ?? "");
  const [editingMapDay, setEditingMapDay] = useState<string | null>(null);

  // Track which day is currently in view so the jump strip highlights
  // the right pill as the user scrolls.
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        // Prefer the entry whose intersection ratio is highest; break
        // ties by earliest (topmost) key.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const first = visible[0];
        if (first) {
          const key = (first.target as HTMLElement).dataset.dayKey;
          if (key) setActiveDay(key);
        }
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: [0, 0.25, 0.5] },
    );
    observers.push(io);
    for (const el of groupRefs.current.values()) io.observe(el);
    return () => {
      for (const o of observers) o.disconnect();
    };
  }, [groups.length]);

  const jumpTo = (key: string) => {
    const el = groupRefs.current.get(key);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* The ad slot lives in the shared trip layout under the hero
          (see trip.$id.tsx), so it renders once per trip screen instead
          of duplicating on Timeline. */}
      {/* No -mx-5 here: unlike Expenses, this container has no px-5 to
          bleed out of, so a negative margin pushed the strip 20px past both
          screen edges — the whole app could then be panned sideways and the
          WebView's enlarged layout viewport dragged the sticky/fixed bars
          under the status bar. Guarded by app-layout.layout.test.tsx. */}
      {groups.length > 1 ? (
        <div
          className="sticky top-[max(2.75rem,var(--safe-top))] z-[5] border-b border-line bg-[color-mix(in_oklab,var(--bg)_88%,transparent)] backdrop-blur-md px-5"
          aria-label="Jump to day"
        >
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto py-2">
            {groups.map((g, i) => {
              const on = g.key === activeDay;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => jumpTo(g.key)}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors",
                    on
                      ? "bg-primary text-white"
                      : "bg-bg-sunken text-ink-muted hover:text-ink",
                  )}
                  aria-current={on ? "true" : undefined}
                >
                  Day {i + 1}
                  {g.subtitle ? ` · ${g.subtitle.slice(0, 3)}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {groups.map((group) => {
        const dayMapsUrl = trip?.days?.[group.key]?.mapsUrl ?? null;
        return (
        <section
          key={group.key}
          data-day-key={group.key}
          ref={(el) => {
            if (el) groupRefs.current.set(group.key, el);
            else groupRefs.current.delete(group.key);
          }}
        >
          {/*
            Two-row day header — top line is the display title + weather,
            bottom line is the weekday·date subheader + day-map chip. Split
            so nothing has to truncate on narrow phones.
          */}
          <header className="px-5 pt-5 pb-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2
                className="font-display text-xl leading-tight tracking-tight text-ink m-0 truncate"
                style={{ fontVariationSettings: `"opsz" 36, "wght" 500` }}
              >
                {group.dayNumberLabel}
              </h2>
              <WeatherPill weather={weather[group.key]} />
            </div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-ink-subtle m-0 truncate">
                {group.subtitle
                  ? `${group.subtitle} · ${group.dateLabel}`
                  : group.dateLabel}
              </p>
              <DayMapChip
                url={dayMapsUrl}
                onEdit={
                  readOnly || group.key === "no-date"
                    ? undefined
                    : () => setEditingMapDay(group.key)
                }
              />
            </div>
          </header>

          {group.items.length === 0 ? (
            <EmptyDayCard tripId={trip?.id} dayKey={group.key} readOnly={readOnly} />
          ) : (
            <ul className="flex flex-col">
              {group.items.map((a) => {
                // Duration bar height: 24 px at 0.5h, cap at 96 px for
                // 4h+. Anchored + undated activities collapse to a small
                // dot so the timeline reads consistently.
                const hasStart = !!a.startTime;
                const barHeight = a.durationHours
                  ? Math.min(96, Math.max(24, a.durationHours * 24))
                  : hasStart
                    ? 20
                    : 8;
                const endLabel = a.endTime ? formatTime(a.endTime) : null;
                const cat = getCategoryMeta(a.category);
                const CatIcon = cat?.icon ?? null;
                const rowInner = (
                  <>
                    <div className="flex w-12 shrink-0 flex-col items-end gap-1.5 pt-0.5">
                      <p className="tabular text-[12px] font-semibold text-ink-muted m-0">
                        {a.startTime ? formatTime(a.startTime) : "—"}
                      </p>
                      {endLabel ? (
                        <p className="tabular text-[11px] text-ink-subtle m-0">
                          {endLabel}
                        </p>
                      ) : null}
                    </div>
                    <div
                      aria-hidden
                      className={cn(
                        "w-1 shrink-0 rounded-full",
                        !cat && hasStart ? "bg-primary" : null,
                        !cat && !hasStart ? "bg-ink-subtle/40" : null,
                      )}
                      style={{
                        height: barHeight,
                        ...(cat ? { backgroundColor: cat.color } : null),
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {CatIcon && cat ? (
                          <span
                            aria-label={cat.label}
                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: cat.softColor, color: cat.color }}
                          >
                            <CatIcon className="h-3 w-3" />
                          </span>
                        ) : null}
                        <p className="text-[15px] font-semibold text-ink m-0 truncate">
                          {a.name}
                        </p>
                      </div>
                      {a.place ? (
                        <p className="mt-0.5 inline-flex items-center gap-1 text-[13px] text-ink-muted">
                          <MapPin className="h-3 w-3" />
                          {a.place}
                        </p>
                      ) : null}
                      {a.description ? (
                        <p className="mt-1 text-[13px] leading-snug text-ink-muted">
                          {a.description}
                        </p>
                      ) : null}
                    </div>
                  </>
                );
                return (
                  <li key={a.id} data-activity-id={a.id} className="border-t border-line first:border-t-0">
                    {readOnly ? (
                      <div className="flex w-full items-start gap-3 px-5 py-3">
                        {rowInner}
                      </div>
                    ) : (
                      // Tap to open the edit sheet; delete lives inside that
                      // sheet. Horizontal swipes are reserved for the page
                      // (tab navigation), so the row no longer captures them.
                      <button
                        type="button"
                        onClick={() => onEdit!(a)}
                        aria-label={`Edit ${a.name}`}
                        className="flex w-full items-start gap-3 px-5 py-3 text-left"
                      >
                        {rowInner}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

        </section>
        );
      })}

      {editingMapDay && trip ? (
        <EditDayMapSheet
          tripId={trip.id}
          dayKey={editingMapDay}
          initialUrl={trip.days?.[editingMapDay]?.mapsUrl ?? ""}
          dayLabel={groups.find((g) => g.key === editingMapDay)?.label ?? ""}
          onClose={() => setEditingMapDay(null)}
        />
      ) : null}
    </div>
  );
}

// ─── Day-header map chip ────────────────────────────────────
// One control per day: opens the pinned Google Maps link if set, else
// prompts the planner to add one. On read-only trips with no link, it
// renders nothing so the header stays clean.
function DayMapChip({
  url,
  onEdit,
}: {
  url: string | null;
  /** Undefined => read-only (no edit affordance shown). */
  onEdit?: () => void;
}) {
  if (url) {
    return (
      <span className="inline-flex items-center gap-0.5">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-semibold text-primary hover:brightness-95"
          aria-label="Open day map"
        >
          <MapIcon className="h-3 w-3" />
          Map
        </a>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
            aria-label="Edit day map link"
          >
            <Pencil className="h-3 w-3" />
          </button>
        ) : null}
      </span>
    );
  }
  if (!onEdit) return null;
  return (
    <button
      type="button"
      onClick={onEdit}
      className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 py-0.5 text-[11px] font-semibold text-ink-subtle hover:border-primary hover:text-primary"
      aria-label="Add map link for day"
    >
      <MapIcon className="h-3 w-3" />
      Map
    </button>
  );
}

// ─── Day-map edit sheet ─────────────────────────────────────
// Small paste-in-URL sheet triggered from the day header. Persists the
// link on the trip doc under days[yyyy-MM-dd].mapsUrl and invalidates
// the trip query so the chip flips to "open" immediately.
function EditDayMapSheet({
  tripId,
  dayKey,
  initialUrl,
  dayLabel,
  onClose,
}: {
  tripId: string;
  dayKey: string;
  initialUrl: string;
  dayLabel: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(initialUrl);

  const saver = useTripMutation({
    keys: [queryKeys.trip(tripId)],
    mutationFn: async (nextUrl: string) => {
      const trimmed = nextUrl.trim();
      if (trimmed) {
        try {
          // Reject anything that isn't a valid http(s) URL before we hit
          // Firestore — the chip renders an <a href> so a bad value would
          // silently open a broken tab.
          const parsed = new URL(trimmed);
          if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            throw new Error("Link must start with http:// or https://");
          }
        } catch {
          throw new Error("That doesn't look like a valid link.");
        }
      }
      await unwrap(setTripDayMapsUrl(tripId, dayKey, trimmed || null));
    },
    errorToast: "Couldn't save link.",
    onSuccess: () => {
      toast.success(url.trim() ? "Map link saved." : "Map link removed.");
      onClose();
    },
  });

  const busy = saver.isPending;

  return (
    <Sheet open guardUnsaved onOpenChange={(open) => !open && !busy && onClose()}>
      <SheetContent>
        <SheetHeader title={dayLabel ? `Map for ${dayLabel}` : "Day map link"} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saver.mutate(url);
          }}
          noValidate
        >
          <SheetBody className="flex flex-col gap-4">
            <Field
              id="daymap-url"
              label="Google Maps link"
              helper="Paste a saved-list or directions URL from Google Maps."
            >
              <Input
                id="daymap-url"
                autoFocus
                type="url"
                inputMode="url"
                placeholder="https://maps.app.goo.gl/…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </Field>
            {initialUrl ? (
              <button
                type="button"
                onClick={() => {
                  setUrl("");
                  saver.mutate("");
                }}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-[13px] font-semibold text-danger hover:bg-danger-soft/40 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Remove link
              </button>
            ) : null}
          </SheetBody>
          <SheetFooter>
            <Button type="submit" block disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button type="button" variant="ghost" block onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty-day placeholder ───────────────────────────────────
// Shown for days inside the trip window that don't have any activity yet.
// On editable trips it deep-links into the add-event flow (with the day's
// date preselected would be nicer, but the current form defaults to now +
// bounds-clamp, which is close enough for MVP). On read-only trips it's
// a muted stub with no CTA.
function EmptyDayCard({
  tripId,
  dayKey,
  readOnly,
}: {
  tripId: string | undefined;
  /** yyyy-MM-dd of the day this card covers — deep-links prefill it. */
  dayKey?: string;
  readOnly: boolean;
}) {
  if (readOnly || !tripId) {
    return (
      <div className="mx-5 mb-2 rounded-lg border border-dashed border-line px-4 py-4 text-center">
        <p className="text-[13px] text-ink-subtle m-0">Nothing planned.</p>
      </div>
    );
  }
  const to =
    dayKey && /^\d{4}-\d{2}-\d{2}$/.test(dayKey)
      ? `/trip/${tripId}/add-event?date=${dayKey}`
      : `/trip/${tripId}/add-event`;
  return (
    <Link
      to={to}
      className="mx-5 mb-2 flex items-center gap-3 rounded-lg border border-dashed border-line px-4 py-4 transition-colors hover:border-primary hover:bg-primary-soft/30"
    >
      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary"
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-ink m-0">Plan this day</p>
        <p className="text-[12px] text-ink-subtle m-0">
          Add a flight, a meal, a hike — anything.
        </p>
      </div>
    </Link>
  );
}

// ─── Edit + delete sheet ─────────────────────────────────────
function EditEventSheet({
  tripId,
  activity,
  onClose,
}: {
  tripId: string;
  activity: Activity;
  onClose: () => void;
}) {
  const { data: trip } = useTrip(tripId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Trip-date bounds close over the current trip — if trip isn't loaded yet,
  // fall back to a wide-open bounds envelope so the form still opens.
  const bounds = trip
    ? getTripDateBounds(trip)
    : {
        minAttr: "",
        maxAttr: "",
        minMs: -Infinity,
        maxMs: Infinity,
        startLabel: "",
        endLabel: "",
      };

  const editEventSchema = z
    .object({
      name: z.string().trim().min(1, "What's it called?"),
      place: z.string().trim().optional(),
      description: z.string().trim().optional(),
      startTime: z.string().optional(),
      endTime: z.string().optional(),
      category: z
        .enum(["stay", "place", "food", "concert", "movie", "beach", "photo"])
        .nullable()
        .optional(),
    })
    .refine((v) => isWithinBounds(v.startTime, bounds), {
      message: bounds.startLabel
        ? `Start must be between ${bounds.startLabel} and ${bounds.endLabel}`
        : "Start out of range",
      path: ["startTime"],
    })
    .refine((v) => isWithinBounds(v.endTime, bounds), {
      message: bounds.startLabel
        ? `End must be between ${bounds.startLabel} and ${bounds.endLabel}`
        : "End out of range",
      path: ["endTime"],
    })
    .refine(
      (v) => !v.startTime || !v.endTime || new Date(v.endTime) >= new Date(v.startTime),
      { message: "End can't be before start", path: ["endTime"] },
    );

  type EditEventValues = z.infer<typeof editEventSchema>;

  const form = useForm<EditEventValues>({
    resolver: zodResolver(editEventSchema),
    defaultValues: {
      name: activity.name,
      place: activity.place ?? "",
      description: activity.description ?? "",
      startTime: toDateTimeLocal(activity.startTime),
      endTime: toDateTimeLocal(activity.endTime),
      category: activity.category ?? null,
    },
  });
  const category = form.watch("category") ?? null;

  const updater = useTripMutation({
    keys: [queryKeys.activities(tripId)],
    mutationFn: async (values: EditEventValues) => {
      const patch: ActivityUpdate = {
        name: values.name.trim(),
        place: values.place?.trim() || null,
        description: values.description?.trim() || null,
        startTime: values.startTime ? Timestamp.fromDate(new Date(values.startTime)) : null,
        endTime: values.endTime ? Timestamp.fromDate(new Date(values.endTime)) : null,
        durationHours:
          values.startTime && values.endTime
            ? Math.max(
                0,
                (new Date(values.endTime).getTime() - new Date(values.startTime).getTime()) /
                  3_600_000,
              )
            : null,
        category: values.category ?? null,
        updatedAt: Timestamp.now(),
      };
      await unwrap(updateActivity(tripId, activity.id, patch));
    },
    successToast: "Event updated.",
    errorToast: "Couldn't save changes.",
    onSuccess: onClose,
  });

  const deleter = useTripMutation({
    keys: [queryKeys.activities(tripId)],
    mutationFn: () => unwrap(deleteActivity(tripId, activity.id)),
    successToast: "Event removed.",
    errorToast: "Couldn't delete.",
    onSuccess: onClose,
  });

  const busy = updater.isPending || deleter.isPending;

  return (
    <>
      <Sheet open guardUnsaved onOpenChange={(open) => !open && !busy && onClose()}>
        <SheetContent>
          <SheetHeader title="Edit event" />
          <form onSubmit={form.handleSubmit((v) => updater.mutate(v))} noValidate>
            <SheetBody className="flex flex-col gap-4">
              <Field id="edev-name" label="What is it?" error={form.formState.errors.name?.message}>
                <Input
                  id="edev-name"
                  autoFocus
                  invalid={!!form.formState.errors.name}
                  {...form.register("name")}
                />
              </Field>
              <Field id="edev-category" label="Category">
                <CategoryPicker
                  value={category}
                  onChange={(next) => form.setValue("category", next, { shouldDirty: true })}
                />
              </Field>
              <Field id="edev-place" label="Where?">
                <Input id="edev-place" {...form.register("place")} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  id="edev-start"
                  label="Starts"
                  error={form.formState.errors.startTime?.message}
                >
                  <Input
                    id="edev-start"
                    type="datetime-local"
                    min={bounds.minAttr || undefined}
                    max={bounds.maxAttr || undefined}
                    {...form.register("startTime")}
                  />
                </Field>
                <Field
                  id="edev-end"
                  label="Ends"
                  error={form.formState.errors.endTime?.message}
                >
                  <Input
                    id="edev-end"
                    type="datetime-local"
                    min={bounds.minAttr || undefined}
                    max={bounds.maxAttr || undefined}
                    {...form.register("endTime")}
                  />
                </Field>
              </div>
              <Field id="edev-desc" label="Anything else? (optional)">
                <Input id="edev-desc" {...form.register("description")} />
              </Field>

              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={busy}
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-[13px] font-semibold text-danger hover:bg-danger-soft/40 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                Remove from timeline
              </button>
            </SheetBody>
            <SheetFooter>
              <Button type="submit" block disabled={busy}>
                {updater.isPending ? "Saving…" : "Save changes"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                block
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Remove "${activity.name}"?`}
        description="It disappears for the whole group."
        confirmLabel="Remove"
        destructive
        onConfirm={async () => {
          await deleter.mutateAsync();
          setConfirmOpen(false);
        }}
      />
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
function LoadingList() {
  return (
    <div className="mx-auto max-w-2xl px-5 pt-6 flex flex-col gap-4" aria-busy>
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-16 w-full rounded-md" />
      <Skeleton className="h-16 w-full rounded-md" />
      <Skeleton className="h-16 w-full rounded-md" />
    </div>
  );
}

/**
 * Bucket activities by their LOCAL yyyy-MM-dd and, when a trip is known,
 * emit a group for every day in the trip window — including empty ones so
 * the UI can prompt "Plan this day" on days without activities.
 *
 * The key subtlety: `Timestamp.toDate().toISOString()` is UTC, so an event
 * at 20:00 local in a west-of-UTC zone (America/Costa_Rica, EST, PST) has
 * a UTC-date one day AFTER its local date. Bucketing by the UTC slice was
 * putting evening events into the next day's group and, worse, colliding
 * with early-morning events on that next day whose label read a day earlier.
 * We always bucket by the local date and label by the local date too.
 */
function groupByDay(activities: Activity[], trip: Trip | undefined) {
  const map = new Map<string, Activity[]>();
  const dayLabels = new Map<string, { label: string; subtitle: string }>();

  const registerDay = (d: Date) => {
    const key = localYmd(d);
    if (!dayLabels.has(key)) {
      dayLabels.set(key, {
        label: d.toLocaleDateString("en-US", { month: "long", day: "numeric" }),
        subtitle: d.toLocaleDateString("en-US", { weekday: "long" }),
      });
    }
    if (!map.has(key)) map.set(key, []);
    return key;
  };

  // Enumerate every day of the trip window first so empty days get a group.
  if (trip) {
    const start = trip.startDate.toDate();
    const end = trip.endDate.toDate();
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const finish = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cursor.getTime() <= finish.getTime()) {
      registerDay(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  // Now drop each activity into its local-date bucket. Activities outside
  // the trip window (rare, e.g. dates edited after events were created) get
  // their own group so nothing silently disappears.
  for (const a of activities) {
    if (a.startTime) {
      const key = registerDay(a.startTime.toDate());
      map.get(key)!.push(a);
    } else {
      if (!map.has("no-date")) {
        map.set("no-date", []);
        dayLabels.set("no-date", { label: "No date", subtitle: "" });
      }
      map.get("no-date")!.push(a);
    }
  }

  // Sort activities within each day by startTime so evening rows sit last.
  for (const items of map.values()) {
    items.sort((a, b) => {
      const at = a.startTime?.toMillis() ?? 0;
      const bt = b.startTime?.toMillis() ?? 0;
      return at - bt;
    });
  }

  // Sort day groups by key (yyyy-MM-dd sorts chronologically); "no-date"
  // sinks to the bottom since 'n' > digits.
  const entries = [...map.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  return entries.map(([key, items], i) => {
    const meta = dayLabels.get(key)!;
    const isNoDate = key === "no-date";
    const dayNumberLabel = isNoDate ? meta.label : `Day ${i + 1}`;
    const dateLabel = isNoDate ? "" : meta.label;
    return {
      key,
      // Kept for consumers (edit-map sheet, jump strip) that want a
      // single string. Timeline header renders dayNumberLabel + dateLabel
      // separately for the two-row layout.
      label: isNoDate ? meta.label : `Day ${i + 1} · ${meta.label}`,
      dayNumberLabel,
      dateLabel,
      subtitle: meta.subtitle,
      items,
    };
  });
}

/** yyyy-MM-dd in the viewer's local timezone. Matches the weather-hook key. */
function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

